/**
 * RFC-4180 CSV row encoding for the scrubbed upload.
 *
 * The browser scrub pass emits ONE canonical format (UTF-8 CSV) regardless of
 * the source format, so the backend always ingests CSV. This module is the pure,
 * isomorphic encoder: it turns a row of cells into a correctly-quoted CSV line
 * and builds the scrubbed header row from a {@link ScrubPlan}.
 *
 * Quoting follows RFC 4180: a field is wrapped in double quotes when it contains
 * a comma, a double quote, CR, or LF; embedded double quotes are doubled. The
 * line terminator is CRLF, per the RFC.
 *
 * Pure and isomorphic: no DOM, no node:*.
 */

import type { ScrubPlan } from './pipeline-types';

/** Characters that force a field to be quoted under RFC 4180. */
const MUST_QUOTE_RE = /[",\r\n]/u;

/** Quote a single CSV field if needed, doubling any embedded double quotes. */
export function encodeCsvField(value: string): string {
  if (!MUST_QUOTE_RE.test(value)) return value;
  return `"${value.replace(/"/gu, '""')}"`;
}

/**
 * Encode one row of cells into a CSV line terminated by CRLF. Each cell is
 * independently quoted/escaped per RFC 4180. An empty `cells` array encodes to
 * just the line terminator (a blank record), which round-trips to a single empty
 * field — callers should not pass an empty row.
 */
export function encodeCsvRow(cells: readonly string[]): string {
  return cells.map(encodeCsvField).join(',') + '\r\n';
}

/**
 * Build the scrubbed header row from a plan: the output columns in source order,
 * with PII-dropped columns removed and Loan/Tax ID columns kept under their
 * original header names (their *values* become protected IDs; the column
 * identity is preserved so the backend can still infer + suggest a mapping for
 * them — PRODUCT.md "All remaining columns are uploaded unchanged" plus the
 * protected-ID substitution).
 *
 * Decision (flagged): the protected-ID columns keep their ORIGINAL header name
 * rather than being renamed (e.g. to "Protected Loan ID"). Renaming would strip
 * the mapping signal the backend's Standard-field suggestion relies on; keeping
 * the name preserves it while the values are still one-way HMACs. See the design
 * doc's "renames Loan/Tax ID" note — interpreted as the value transform, not a
 * header rename.
 */
export function scrubbedHeaderNames(plan: ScrubPlan): string[] {
  return scrubbedColumnIndexes(plan).map((index) => {
    const header = plan.headers.find((h) => h.index === index);
    return header?.name ?? '';
  });
}

/** Convenience: the scrubbed header row already CSV-encoded (with CRLF). */
export function scrubbedHeaderRow(plan: ScrubPlan): string {
  return encodeCsvRow(scrubbedHeaderNames(plan));
}

/**
 * The name a given ORIGINAL-header column index carries in the SCRUBBED output, or
 * null when that column does not survive scrubbing (it was dropped as PII) or the
 * index isn't designated. Loan/Tax ID columns are HMAC'd in place and keep their
 * ORIGINAL header name (see {@link scrubbedHeaderNames}), so this returns that name
 * — which is exactly what the upload-time ingest looks for in the scrubbed header to
 * pin each row's protected IDs. The preflight decision carries Loan/Tax ID indexes
 * into the original headers; this maps one to the scrubbed-output column name to
 * send at transfer-init.
 *
 * Pure and isomorphic.
 */
export function scrubbedNameForIndex(plan: ScrubPlan, index: number | null): string | null {
  if (index === null) return null;
  if (plan.droppedColumnIndexes.has(index)) return null;
  const header = plan.headers.find((h) => h.index === index);
  return header?.name ?? null;
}

/**
 * The source column indexes that survive scrubbing, in ascending source order:
 * every column except those the user marked PII (dropped). The Loan ID column is
 * always included (required) and a selected Tax ID column is included; both keep
 * their position and get protected values in the row builder. An UNselected Tax
 * ID has no column index in the plan (`taxIdColumnIndex === null`), so it is
 * simply a normal pass-through column unless the user also dropped it. This is
 * the single ordering the row builder and the header builder must agree on, so
 * it is derived once here.
 */
export function scrubbedColumnIndexes(plan: ScrubPlan): number[] {
  return [...plan.headers]
    .sort((a, b) => a.index - b.index)
    .map((h) => h.index)
    .filter((index) => !plan.droppedColumnIndexes.has(index));
}
